import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import type { Attribute, ProductTypeAttribute } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@nucleus-commerce/ui/components/command';
import { FormField } from '@nucleus-commerce/ui/components/form';
import { Popover, PopoverContent, PopoverTrigger } from '@nucleus-commerce/ui/components/popover';
import { Switch } from '@nucleus-commerce/ui/components/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import type { ProductTypeFormInput } from '../schemas/product-type-form-schema';

type Shown = Pick<Attribute, 'key' | 'label' | 'dataType'>;

export interface ProductTypeAttributesSectionProps {
  /** The organization's attribute dictionary: what the picker offers. */
  attributes: readonly Attribute[];
  /** What the saved type says about its assignments, for labels the dictionary lacks. */
  assigned: readonly ProductTypeAttribute[];
  readOnly: boolean;
}

function AttributePicker({
  options,
  onPick,
}: {
  options: readonly Attribute[];
  onPick: (attribute: Attribute) => void;
}) {
  const { t } = useTranslation('productTypes');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const visible = needle
    ? options.filter(
        (a) => a.label.toLowerCase().includes(needle) || a.key.toLowerCase().includes(needle),
      )
    : options;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="self-start">
          <Plus aria-hidden />
          {t('assignments.add')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t('assignments.search')}
            aria-label={t('assignments.search')}
          />
          <CommandList>
            <CommandEmpty>
              {options.length === 0 ? t('assignments.allAssigned') : t('assignments.noMatch')}
            </CommandEmpty>
            {visible.length > 0 ? (
              <CommandGroup>
                {visible.map((attribute) => (
                  <CommandItem
                    key={attribute.id}
                    value={attribute.id}
                    onSelect={() => {
                      onPick(attribute);
                      setOpen(false);
                      setQuery('');
                    }}
                  >
                    <span className="flex-1">{attribute.label}</span>
                    <code className="text-muted-foreground text-xs">{attribute.key}</code>
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Pick attributes from the dictionary, mark each required or optional, and order them. */
export function ProductTypeAttributesSection({
  attributes,
  assigned,
  readOnly,
}: ProductTypeAttributesSectionProps) {
  const { t } = useTranslation('productTypes');
  const form = useFormContext<ProductTypeFormInput>();
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name: 'attributes',
  });

  const shown = useMemo(() => {
    const map = new Map<string, Shown>(assigned.map((a) => [a.attributeId, a]));
    for (const a of attributes) map.set(a.id, a);
    return map;
  }, [attributes, assigned]);
  const taken = new Set(fields.map((f) => f.attributeId));
  const options = attributes.filter((a) => !taken.has(a.id));
  const errors = form.formState.errors.attributes;
  const listError = errors?.message ?? errors?.root?.message;

  return (
    <FormSection title={t('sections.attributes')} description={t('sections.attributesDescription')}>
      {fields.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('assignments.none')}</p>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <Table aria-label={t('assignments.label')}>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-4">{t('assignments.attribute')}</TableHead>
                <TableHead>{t('assignments.type')}</TableHead>
                <TableHead>{t('assignments.required')}</TableHead>
                <TableHead className="pr-4 text-right">{t('assignments.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {fields.map((field, index) => {
                const attribute = shown.get(field.attributeId);
                const label = attribute?.label ?? t('assignments.unknown');
                return (
                  <TableRow key={field.id} aria-label={label}>
                    <TableCell className="pl-4">
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <span className="font-medium">{label}</span>
                        <code className="text-muted-foreground text-xs">
                          {attribute?.key ?? field.attributeId}
                        </code>
                      </div>
                    </TableCell>
                    <TableCell>
                      {attribute ? (
                        <Badge variant="secondary" className="font-normal">
                          {t(`dataType.${attribute.dataType}`)}
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <FormField
                        control={form.control}
                        name={`attributes.${index}.isRequired`}
                        render={({ field: required }) => (
                          <Switch
                            checked={required.value}
                            onCheckedChange={required.onChange}
                            disabled={readOnly}
                            aria-label={t('assignments.requiredToggle', { label })}
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell className="pr-4 text-right">
                      {readOnly ? null : (
                        <div className="flex justify-end gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('assignments.moveUp', { label })}
                            disabled={index === 0}
                            onClick={() => move(index, index - 1)}
                          >
                            <ArrowUp aria-hidden />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={t('assignments.moveDown', { label })}
                            disabled={index === fields.length - 1}
                            onClick={() => move(index, index + 1)}
                          >
                            <ArrowDown aria-hidden />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            className="text-destructive hover:text-destructive"
                            aria-label={t('assignments.remove', { label })}
                            onClick={() => remove(index)}
                          >
                            <X aria-hidden />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {listError ? <p className="text-destructive text-sm">{listError}</p> : null}
      {readOnly ? null : (
        <AttributePicker
          options={options}
          onPick={(attribute) => append({ attributeId: attribute.id, isRequired: false })}
        />
      )}
    </FormSection>
  );
}
